"use client";

import {
	Activity as LucideActivity,
	ArrowDown as LucideArrowDown,
	ArrowLeft as LucideArrowLeft,
	ArrowRight as LucideArrowRight,
	ArrowUp as LucideArrowUp,
	ArrowUpDown as LucideArrowUpDown,
	ArrowUpRight as LucideArrowUpRight,
	BarChart3 as LucideBarChart3,
	BedDouble as LucideBedDouble,
	Calendar as LucideCalendar,
	CalendarCheck2 as LucideCalendarCheck2,
	CalendarClock as LucideCalendarClock,
	CalendarRange as LucideCalendarRange,
	CalendarX2 as LucideCalendarX2,
	Car as LucideCar,
	ChartLine as LucideChartLine,
	Check as LucideCheck,
	CheckCircle2 as LucideCheckCircle2,
	ChevronDown as LucideChevronDown,
	ChevronLeft as LucideChevronLeft,
	ChevronRight as LucideChevronRight,
	ChevronsUpDown as LucideChevronsUpDown,
	ChevronUp as LucideChevronUp,
	Clock as LucideClock,
	Command as LucideCommand,
	Database as LucideDatabase,
	Eye as LucideEye,
	Filter as LucideFilter,
	Flag as LucideFlag,
	Globe as LucideGlobe,
	Grid as LucideGrid,
	type LucideIcon,
	Info as LucideInfo,
	LayoutGrid as LucideLayoutGrid,
	Loader2 as LucideLoader2,
	LockKeyhole as LucideLockKeyhole,
	LogOut as LucideLogOut,
	Mail as LucideMail,
	MapPin as LucideMapPin,
	Medal as LucideMedal,
	Menu as LucideMenu,
	PanelLeft as LucidePanelLeft,
	Pencil as LucidePencil,
	Plus as LucidePlus,
	type LucideProps,
	RefreshCw as LucideRefreshCw,
	Ruler as LucideRuler,
	Search as LucideSearch,
	Shield as LucideShield,
	ShieldAlert as LucideShieldAlert,
	Star as LucideStar,
	Tent as LucideTent,
	Trash2 as LucideTrash2,
	TrendingUp as LucideTrendingUp,
	TriangleAlert as LucideTriangleAlert,
	Trophy as LucideTrophy,
	UserRound as LucideUserRound,
	UserSearch as LucideUserSearch,
	Users as LucideUsers,
	UsersRound as LucideUsersRound,
	UtensilsCrossed as LucideUtensilsCrossed,
	Volleyball as LucideVolleyball,
	Wifi as LucideWifi,
	Wrench as LucideWrench,
	X as LucideX,
} from "lucide-react";
import type { ReactElement } from "react";

type AppIcon = ((
	props: {
		className?: string;
		color?: string;
		size?: number;
	} & LucideProps,
) => ReactElement) & {
	displayName?: string;
};

function makeLucide(Inner: LucideIcon, name: string): AppIcon {
	const Wrapped: AppIcon = (props) => (
		<Inner color="currentColor" size={20} strokeWidth={2.25} {...props} />
	);
	Wrapped.displayName = name;
	return Wrapped;
}

export const Search = makeLucide(LucideSearch, "Search");
export const ArrowLeft = makeLucide(LucideArrowLeft, "ArrowLeft");
export const ArrowRight = makeLucide(LucideArrowRight, "ArrowRight");
export const ArrowDown = makeLucide(LucideArrowDown, "ArrowDown");
export const ArrowUp = makeLucide(LucideArrowUp, "ArrowUp");
export const ArrowUpRight = makeLucide(LucideArrowUpRight, "ArrowUpRight");
export const Calendar = makeLucide(LucideCalendar, "Calendar");
export const CalendarRange = makeLucide(LucideCalendarRange, "CalendarRange");
export const CalendarClock = makeLucide(LucideCalendarClock, "CalendarClock");
export const CalendarX2 = makeLucide(LucideCalendarX2, "CalendarX2");
export const CalendarCheck2 = makeLucide(LucideCalendarCheck2, "CalendarCheck2");
export const Car = makeLucide(LucideCar, "Car");
export const BarChart3 = makeLucide(LucideBarChart3, "BarChart3");
export const ChartLine = makeLucide(LucideChartLine, "ChartLine");
export const ChartUp = makeLucide(LucideTrendingUp, "ChartUp");
export const Check = makeLucide(LucideCheck, "Check");
export const CheckCircle2 = makeLucide(LucideCheckCircle2, "CheckCircle2");
export const AlertTriangle = makeLucide(LucideTriangleAlert, "AlertTriangle");
export const TriangleAlert = AlertTriangle;
export const ShieldAlert = makeLucide(LucideShieldAlert, "ShieldAlert");
export const Flag = makeLucide(LucideFlag, "Flag");
export const Globe = makeLucide(LucideGlobe, "Globe");
export const LockKeyhole = makeLucide(LucideLockKeyhole, "LockKeyhole");
export const Mail = makeLucide(LucideMail, "Mail");
export const MapPin = makeLucide(LucideMapPin, "MapPin");
export const Plus = makeLucide(LucidePlus, "Plus");
export const RefreshCw = makeLucide(LucideRefreshCw, "RefreshCw");
export const Ruler = makeLucide(LucideRuler, "Ruler");
export const Tent = makeLucide(LucideTent, "Tent");
export const Tool = makeLucide(LucideWrench, "Tool");
export const Trophy = makeLucide(LucideTrophy, "Trophy");
export const Medal = makeLucide(LucideMedal, "Medal");
export const Users = makeLucide(LucideUsers, "Users");
export const UsersRound = makeLucide(LucideUsersRound, "UsersRound");
export const UserRound = makeLucide(LucideUserRound, "UserRound");
export const Users2 = Users;
export const UserSearch = makeLucide(LucideUserSearch, "UserSearch");
export const Star = makeLucide(LucideStar, "Star");
export const BedDouble = makeLucide(LucideBedDouble, "BedDouble");
export const Wifi = makeLucide(LucideWifi, "Wifi");
export const ArrowUpDown = makeLucide(LucideArrowUpDown, "ArrowUpDown");
export const Pencil = makeLucide(LucidePencil, "Pencil");
export const UtensilsCrossed = makeLucide(LucideUtensilsCrossed, "UtensilsCrossed");
export const Filter = makeLucide(LucideFilter, "Filter");
export const View = makeLucide(LucideEye, "View");
export const Clock = makeLucide(LucideClock, "Clock");
export const Command = makeLucide(LucideCommand, "Command");
export const Database = makeLucide(LucideDatabase, "Database");
export const Volleyball = makeLucide(LucideVolleyball, "Volleyball");
export const Info = makeLucide(LucideInfo, "Info");
export const Activity = makeLucide(LucideActivity, "Activity");
export const TrendingUp = makeLucide(LucideTrendingUp, "TrendingUp");
export const LayoutGrid = makeLucide(LucideLayoutGrid, "LayoutGrid");
export const Menu = makeLucide(LucideMenu, "Menu");
export const PanelLeft = makeLucide(LucidePanelLeft, "PanelLeft");
export const Shield = makeLucide(LucideShield, "Shield");
export const LogOut = makeLucide(LucideLogOut, "LogOut");
export const Exit = LogOut;
export const Analytics = makeLucide(LucideBarChart3, "Analytics");
export const Grid = makeLucide(LucideGrid, "Grid");
export const ChevronDown = makeLucide(LucideChevronDown, "ChevronDown");
export const ChevronUp = makeLucide(LucideChevronUp, "ChevronUp");
export const ChevronLeft = makeLucide(LucideChevronLeft, "ChevronLeft");
export const ChevronRight = makeLucide(LucideChevronRight, "ChevronRight");
export const ChevronsUpDown = makeLucide(LucideChevronsUpDown, "ChevronsUpDown");
export const ChevronDownIcon = ChevronDown;
export const ChevronUpIcon = ChevronUp;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;
export const ChevronsUpDownIcon = ChevronsUpDown;
export const Spinner = makeLucide(LucideLoader2, "Spinner");
export const Loader2Icon = Spinner;
export const Trash2 = makeLucide(LucideTrash2, "Trash2");
export const X = makeLucide(LucideX, "X");
export const XIcon = X;

export type { AppIcon };
